import type { components } from "@/client/backend/zz_schema";

export type Organization = components["schemas"]["Organization"];

// orgRequestOptions pins the organization to a single SDK request instead of
// relying on the region client's default header, so a response is always
// scoped to the same organization as the SWR key it gets cached under, even
// when the request was issued from a render that predates an org switch.
export function orgRequestOptions(organizationTid?: string) {
	return organizationTid
		? { headers: { "X-Limrun-Organization": organizationTid } }
		: undefined;
}

// orderedOrganizations returns the user's organizations with the selected one
// first (so the common case resolves without waiting on the others) and
// duplicates removed.
export function orderedOrganizations(
	selectedOrganization: Organization,
	organizations: Organization[],
): Organization[] {
	const seen = new Set<string>();
	return [selectedOrganization, ...organizations].filter((organization) => {
		if (seen.has(organization.tid)) return false;
		seen.add(organization.tid);
		return true;
	});
}

// Check the preferred organization before searching the others. The parallel
// fallback returns the first match without waiting for unrelated requests.
// Failed probes count as misses, including permission errors and 404s.
export async function findOwningOrganization(
	organizations: Organization[],
	probe: (organization: Organization) => Promise<boolean>,
	preferredOrganization?: Organization,
): Promise<Organization | undefined> {
	const matches = async (organization: Organization) => {
		try {
			return await probe(organization);
		} catch {
			return false;
		}
	};
	if (preferredOrganization && await matches(preferredOrganization)) return preferredOrganization;
	const candidates = organizations.filter(org => org.tid !== preferredOrganization?.tid);
	if (!candidates.length) return undefined;
	return new Promise(resolve => {
		let remaining = candidates.length;
		for (const organization of candidates) {
			void matches(organization).then(found => {
				if (found) resolve(organization);
				if (--remaining === 0) resolve(undefined);
			});
		}
	});
}
